import { Goal, initialDateFormat, Timestamp } from '@griply/types';
import { getStartTillEndLabel, getWeekStartsOn } from '@griply/utils';
import { useUpdateGoal } from 'features/goals';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Flag } from 'shared/assets/icons';
import {
  DatepickerWithOptions,
  IconOptions,
} from 'shared/components/ui/datepicker-with-options';
import { TimeLeft } from 'shared/components/ui/time-left';
import { useToday } from 'shared/contexts/today';
import { useOpenMenu } from 'shared/hooks/use-open-menu';
import { useUser } from 'shared/hooks/use-user';

import * as Styled from './goal-detail-header.style';
import { MetaRow } from './meta-row';

export type DeadlinePickerRowProps = {
  goal: Goal;
  isEditable: boolean;
};

export const DeadlinePickerRow: React.FC<DeadlinePickerRowProps> = ({
  goal,
  isEditable,
}) => {
  const { t } = useTranslation();
  const today = useToday();
  const user = useUser();
  const { submit: updateGoal } = useUpdateGoal();
  const deadlineRef = useRef<HTMLSpanElement>(null);
  const { menuOpen, openMenu, closeMenu } = useOpenMenu();

  const onDeadlineChange = (date?: Timestamp) => {
    updateGoal({ id: goal.id, deadline: date ?? null });
    closeMenu();
  };

  return (
    <>
      <MetaRow
        ref={deadlineRef}
        icon={Flag}
        label={t('forms.goal.fields.deadline.label')}
        isClickable={isEditable}
        isPlaceholder={!goal.deadline}
        onClick={openMenu}
      >
        {goal.deadline ? (
          <Styled.DeadlineValue>
            {getStartTillEndLabel({
              startDate: goal.deadline,
              isShort: true,
              today,
              t,
            })}
            {!goal.completedAt && (
              <Styled.DeadlineTimeLeft>
                <TimeLeft date={goal.deadline} isMidShort />
              </Styled.DeadlineTimeLeft>
            )}
          </Styled.DeadlineValue>
        ) : (
          t('forms.goal.fields.deadline.placeholder')
        )}
      </MetaRow>

      {menuOpen && (
        <DatepickerWithOptions
          referenceElement={deadlineRef}
          value={goal.deadline ?? undefined}
          onChange={onDeadlineChange}
          dateFormat={user?.settings?.dateFormat ?? initialDateFormat}
          weekStartsOn={getWeekStartsOn(user)}
          icon={IconOptions.Calendar}
          canRemoveValue={!!goal.deadline}
          onOutside={closeMenu}
        />
      )}
    </>
  );
};
